import React, { useEffect, useRef, useState } from 'react';
import type { ApiTemplate } from '@/src/types';
import type { TemplatePreviewClip } from './templatePreviews';
import { shouldAutoplayCardLoops } from './mediaPolicy';
import { useLoopSlot } from './livePreviewScheduler';
import { useSignedMedia } from '@/src/infrastructure/media/useSignedMedia';

/** Small "Live" pill used on animated templates in both catalog views. */
export function LiveBadge({ className = '' }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-white shadow ${className}`}
    >
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-white motion-safe:animate-pulse" />
      Live
    </span>
  );
}

function LoopVideo({ clip }: { clip: TemplatePreviewClip }) {
  const ref = useRef<HTMLVideoElement>(null);
  const webmMedia = useSignedMedia(clip.webm);
  const mp4Media = useSignedMedia(clip.mp4);
  const posterMedia = useSignedMedia(clip.poster);
  const webmSrc = webmMedia.src;
  const mp4Src = mp4Media.src;
  const posterSrc = posterMedia.src;
  const sourcesKey = `${webmSrc ?? ''}\u0000${mp4Src ?? ''}`;
  const prevSourcesRef = useRef<string | null>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    video.muted = true;
    try {
      const played = video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});
    } catch {
      /* jsdom */
    }
    return () => {
      // Unload: stop decoding and release the network/decoder resources right away.
      try {
        video.pause();
        video.querySelectorAll('source').forEach((s) => s.removeAttribute('src'));
        video.removeAttribute('src');
        video.load();
      } catch {
        /* jsdom */
      }
    };
  }, []);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const prev = prevSourcesRef.current;
    prevSourcesRef.current = sourcesKey;
    if (prev === null || prev === sourcesKey) return;
    video.load();
    try {
      const played = video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});
    } catch {
      /* jsdom */
    }
  }, [sourcesKey]);
  return (
    <video
      ref={ref}
      data-testid="wow-loop-video"
      className="absolute inset-0 h-full w-full object-cover"
      muted
      loop
      playsInline
      preload="none"
      poster={posterSrc}
      aria-hidden="true"
      tabIndex={-1}
    >
      {webmSrc !== undefined && <source src={webmSrc} type="video/webm" onError={webmMedia.onError} />}
      {mp4Src !== undefined && <source src={mp4Src} type="video/mp4" onError={mp4Media.onError} />}
    </video>
  );
}

/**
 * Card media for the "Wow · Live animated" tab: shows the recorded poster first, then plays the
 * pre-recorded muted loop only while the card is in the viewport and holds a play slot (see
 * livePreviewScheduler — 2 at a time on phones, 3 on desktop). Leaving the viewport unloads the
 * video. With prefers-reduced-motion or Save-Data / 2G, nothing autoplays: the visitor gets a
 * tap-to-play button instead. Opening the full live template stays on the card's Preview button.
 */
export function WowPreviewCard({
  template,
  clip,
  onOpen,
}: {
  template: ApiTemplate;
  clip: TemplatePreviewClip;
  onOpen?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [autoplay] = useState(shouldAutoplayCardLoops);
  const [tapped, setTapped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry) setInView(entry.isIntersecting && entry.intersectionRatio >= 0.35);
      },
      { threshold: [0, 0.35, 0.7] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) setTapped(false);
  }, [inView]);

  const granted = useLoopSlot(template.id, inView && autoplay);
  const playing = autoplay ? granted : tapped;
  const posterMedia = useSignedMedia(clip.poster);

  return (
    <div
      ref={ref}
      data-testid="wow-card"
      data-template-id={template.id}
      data-playing={playing ? 'true' : 'false'}
      className="relative h-full w-full overflow-hidden bg-black"
    >
      <img
        src={posterMedia.src}
        onError={posterMedia.onError}
        alt={template.name}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {playing && <LoopVideo clip={clip} />}
      {onOpen && (
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Open live preview of ${template.name}`}
          className="absolute inset-0 z-[1] cursor-pointer focus-visible:outline focus-visible:outline-4 focus-visible:outline-primary"
        />
      )}
      {!autoplay && (
        <button
          type="button"
          data-testid="wow-tap-to-play"
          aria-pressed={tapped}
          aria-label={`${tapped ? 'Pause' : 'Play'} animated preview of ${template.name}`}
          onClick={(event) => {
            event.stopPropagation();
            setTapped((t) => !t);
          }}
          className="absolute bottom-3 right-3 z-[2] flex h-11 w-11 items-center justify-center rounded-full bg-black/65 text-white shadow-lg backdrop-blur"
        >
          <span aria-hidden="true">{tapped ? '❚❚' : '▶'}</span>
        </button>
      )}
    </div>
  );
}
